import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, authHelpers, bookingHelpers } from '../../lib/supabase';
import SeoHead from '../../components/base/SeoHead';
import CourseImmersive from './components/CourseImmersive';

interface Course {
  id: number;
  name: string;
  duration: string;
  price: number;
  level: string;
  description: string;
  image: string;
  images: string[];
  includes: string[];
  dates: string[];
  tags: string[];
  features: { title: string; desc: string }[];
}

export default function WorkshopPage() {
  const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    course: '',
    date: '',
    time: '',
    name: '',
    phone: '',
    email: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [courses, setCourses] = useState<any[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [teacherNameMap, setTeacherNameMap] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  // 取得目前選中的課程物件
  const getSelectedCourse = () => courses.find(c => c.name === formData.course) || null;

  // 自動取得講師名稱（單一老師才自動帶入）
  const getAutoInstructor = (): string => {
    const course = getSelectedCourse();
    if (!course || !course.teacher_ids || course.teacher_ids.length !== 1) return '';
    return teacherNameMap[course.teacher_ids[0]] || '';
  };

  const [flexibleSlots, setFlexibleSlots] = useState<{ time: string; available: boolean }[]>([]);
  const [loadingFlexibleSlots, setLoadingFlexibleSlots] = useState(false);

  // 判斷日期是否對選中課程可用
  const isDateAvailableForCourse = (dateStr: string): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(dateStr);
    if (selected < today) return false;

    const course = getSelectedCourse();
    if (!course) return true;

    if (course.is_unlimited_dates) {
      const WEEK_DAY_MAP: Record<string, number> = {
        sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
        thursday: 4, friday: 5, saturday: 6,
      };
      const dayOfWeek = selected.getDay();
      // Use mode-specific slots
      const slotsToCheck = course.booking_mode === 'flexible'
        ? (course.flexible_time_slots?.length > 0 ? course.flexible_time_slots : course.available_time_slots)
        : (course.fixed_time_slots?.length > 0 ? course.fixed_time_slots : course.available_time_slots);
      return slotsToCheck.some((slot: any) => WEEK_DAY_MAP[slot.day] === dayOfWeek);
    }

    if (!course.is_unlimited_dates && course.dates?.length > 0) {
      return course.dates.some((d: string) => d.split(' ')[0] === dateStr);
    }

    return true;
  };

  // 取得固定模式的時間段字串列表
  const getAvailableTimeSlots = (): string[] => {
    const course = getSelectedCourse();
    if (!course) return ['10:00', '14:00', '16:00', '19:00'];

    if (course.is_unlimited_dates) {
      if (course.booking_mode === 'flexible') {
        // flexible 模式由 flexibleSlots state 處理，這裡不需要
        return [];
      }
      // 固定班次
      const fixedSlots = course.fixed_time_slots?.length > 0 ? course.fixed_time_slots : course.available_time_slots;
      return fixedSlots.map((slot: any) => `${slot.startTime}-${slot.endTime}`);
    }

    if (!course.is_unlimited_dates && course.dates?.length > 0) {
      const selectedDateEntries = course.dates.filter((d: string) => d.split(' ')[0] === formData.date);
      if (selectedDateEntries.length > 0) {
        return selectedDateEntries.map((d: string) => d.split(' ').slice(1).join(' ')).filter(Boolean);
      }
      const allTimes = course.dates.map((d: string) => d.split(' ').slice(1).join(' ')).filter(Boolean);
      return [...new Set(allTimes)] as string[];
    }

    return ['10:00', '14:00', '16:00', '19:00'];
  };

  // 計算客製化時段（含已預約衝突檢查）
  const computeFlexibleSlots = async (course: any, dateStr: string) => {
    setLoadingFlexibleSlots(true);
    setFlexibleSlots([]);
    try {
      const date = new Date(dateStr + 'T00:00:00');
      const dayOfWeek = date.getDay();
      const WEEK_DAY_MAP: Record<string, number> = {
        sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
        thursday: 4, friday: 5, saturday: 6,
      };
      const windows = (course.flexible_time_slots?.length > 0 ? course.flexible_time_slots : course.available_time_slots || [])
        .filter((s: any) => WEEK_DAY_MAP[s.day] === dayOfWeek);

      if (windows.length === 0) {
        setFlexibleSlots([]);
        return;
      }

      const durationMin = course.slot_duration_minutes || 180;
      const stepMin = 60; // 每小時為間隔

      const allSlots: string[] = [];
      for (const win of windows) {
        const [sh, sm] = win.startTime.split(':').map(Number);
        const [eh, em] = win.endTime.split(':').map(Number);
        const winStart = sh * 60 + sm;
        const winEnd = eh * 60 + em;

        let cur = winStart;
        while (cur + durationMin <= winEnd) {
          const sH = Math.floor(cur / 60), sM = cur % 60;
          const eMin = cur + durationMin, eH = Math.floor(eMin / 60), eM = eMin % 60;
          const t = `${String(sH).padStart(2,'0')}:${String(sM).padStart(2,'0')}-${String(eH).padStart(2,'0')}:${String(eM).padStart(2,'0')}`;
          if (!allSlots.includes(t)) allSlots.push(t);
          cur += stepMin;
        }
      }

      // 查詢該課程、該日期的已有預約（排除已取消）
      const { data: existingBookings } = await supabase
        .from('bookings')
        .select('course_time, status')
        .eq('course_name', course.name)
        .eq('course_date', dateStr)
        .neq('status', '已取消');

      const bookedRanges: { start: number; end: number }[] = (existingBookings || []).map((b: any) => {
        const t = (b.course_time || '').trim();
        const sep = t.includes('-') ? '-' : '~';
        const parts = t.split(sep);
        if (parts.length < 2) return null;
        const [sStr, eStr] = parts;
        const [bsh, bsm] = sStr.trim().split(':').map(Number);
        const [beh, bem] = eStr.trim().split(':').map(Number);
        if (isNaN(bsh) || isNaN(beh)) return null;
        return { start: bsh * 60 + bsm, end: beh * 60 + bem };
      }).filter(Boolean) as { start: number; end: number }[];

      const result = allSlots.map(slotStr => {
        const sep = slotStr.includes('-') ? '-' : '~';
        const [s, e] = slotStr.split(sep);
        const [ssh, ssm] = s.split(':').map(Number);
        const [seh, sem] = e.split(':').map(Number);
        const slotStart = ssh * 60 + ssm, slotEnd = seh * 60 + sem;
        const isBooked = bookedRanges.some(r => slotStart < r.end && slotEnd > r.start);
        return { time: slotStr, available: !isBooked };
      });

      setFlexibleSlots(result);
    } catch (err) {
      console.error('計算時段失敗:', err);
      setFlexibleSlots([]);
    } finally {
      setLoadingFlexibleSlots(false);
    }
  };

  // 當日期或課程變更時，重新計算客製化時段
  useEffect(() => {
    if (formData.date && formData.course) {
      const course = getSelectedCourse();
      if (course?.is_unlimited_dates && course?.booking_mode === 'flexible') {
        computeFlexibleSlots(course, formData.date);
      } else {
        setFlexibleSlots([]);
      }
    } else {
      setFlexibleSlots([]);
    }
  }, [formData.date, formData.course]);

  useEffect(() => {
    window.scrollTo(0, 0);
    checkUser();
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setCoursesLoading(true);
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('is_published', true)
        .neq('show_on_workshop', false)
        .order('workshop_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      const mapped = (data || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        duration: c.duration,
        price: c.price,
        level: c.level,
        description: c.description,
        image: c.image,
        images: (c.images && c.images.length > 0) ? c.images : (c.image ? [c.image] : []),
        includes: c.includes || [],
        dates: c.available_dates || [],
        tags: c.tags || [],
        features: c.features || [],
        is_unlimited_dates: c.is_unlimited_dates || false,
        available_time_slots: c.available_time_slots || [],
        fixed_time_slots: c.fixed_time_slots || [],
        flexible_time_slots: c.flexible_time_slots || [],
        teacher_ids: c.teacher_ids || (c.teacher_id ? [c.teacher_id] : []),
        booking_mode: c.booking_mode || 'weekly_fixed',
        slot_duration_minutes: c.slot_duration_minutes || 180,
      }));
      setCourses(mapped);

      const allTeacherIds = [...new Set(mapped.flatMap((c: any) => c.teacher_ids))].filter(Boolean);
      if (allTeacherIds.length > 0) {
        const { data: teacherProfiles } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', allTeacherIds);
        const tMap: Record<string, string> = {};
        (teacherProfiles || []).forEach((t: any) => { tMap[t.id] = t.name; });
        setTeacherNameMap(tMap);
      }
    } catch (e) {
      console.error('載入課程失敗:', e);
      setCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  };

  const checkUser = async () => {
    try {
      const user = await authHelpers.getCurrentUser();
      if (user) {
        setCurrentUser(user);
        const profile = await authHelpers.getUserProfile(user.id);
        setUserProfile(profile);
        setFormData(prev => ({
          ...prev,
          name: profile.name || '',
          phone: profile.phone || '',
          email: user.email || ''
        }));
      }
    } catch (error) {
      console.error('Check user error:', error);
    }
  };

  const timeSlots = ['10:00', '14:00', '16:00', '19:00'];

  // Calendar functions
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const formatDate = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const handleDateSelect = (year: number, month: number, day: number) => {
    const dateStr = formatDate(year, month, day);
    if (isDateAvailableForCourse(dateStr)) {
      setFormData({ ...formData, date: dateStr, time: '' }); // 重置時間
      setShowCalendar(false);
    }
  };

  const changeMonth = (offset: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.course || !formData.date || !formData.time || !formData.name || !formData.phone || !formData.email) {
      setSubmitMessage('請填寫所有必填欄位');
      return;
    }

    if (formData.phone.length !== 10) {
      setSubmitMessage('請輸入正確的手機號碼（10位數字）');
      return;
    }

    if (formData.notes.length > 500) {
      setSubmitMessage('備註不能超過500字');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // 如果用戶已登入，儲存到 Supabase
      if (currentUser) {
        const autoInstructor = getAutoInstructor();
        await bookingHelpers.createBooking(currentUser.id, {
          courseName: formData.course,
          date: formData.date,
          time: formData.time,
          notes: formData.notes,
          phone: formData.phone,
          instructor: autoInstructor,
        });

        setSubmitMessage('預約成功！您可以在會員中心查看預約記錄');
        
        // 顯示成功訊息
        const successMsg = document.createElement('div');
        successMsg.className = 'fixed top-24 right-6 bg-green-500 text-white px-6 py-3 rounded-full shadow-lg z-50';
        successMsg.textContent = '預約成功！';
        document.body.appendChild(successMsg);
        setTimeout(() => successMsg.remove(), 3000);

        // 重置表單（保留會員資料）
        setFormData({
          course: '',
          date: '',
          time: '',
          name: userProfile?.name || '',
          phone: userProfile?.phone || '',
          email: currentUser.email || '',
          notes: ''
        });
      } else {
        // 未登入用戶使用表單提交
        const submitData = new URLSearchParams();
        submitData.append('course', formData.course);
        submitData.append('date', formData.date);
        submitData.append('time', formData.time);
        submitData.append('name', formData.name);
        submitData.append('phone', formData.phone);
        submitData.append('email', formData.email);
        submitData.append('notes', formData.notes);

        const response = await fetch('https://readdy.ai/api/form/d4mm1ev7s74mkk8pe0n0', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: submitData.toString()
        });

        if (response.ok) {
          setSubmitMessage('預約成功！我們會盡快與您聯繫確認');
          setFormData({
            course: '',
            date: '',
            time: '',
            name: '',
            phone: '',
            email: '',
            notes: ''
          });
        } else {
          setSubmitMessage('預約失敗，請稍後再試');
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitMessage('預約失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const monthNames = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];
  const dayNames = ['日', '一', '二', '三', '四', '五', '六'];

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mofu.com.tw';

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${siteUrl}/workshop#webpage`,
        "url": `${siteUrl}/workshop`,
        "name": "香氛實驗室課程 - 手作香氛蠟燭與調配 | MOFU 淨寵居",
        "description": "MOFU 淨寵居香氛實驗室提供專業手作香氛課程，包括香氛蠟燭製作、香氛調配、擴香製作、手工皂製作等。小班制教學，專業講師指導。",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首頁", "item": siteUrl },
            { "@type": "ListItem", "position": 2, "name": "香氛實驗室", "item": `${siteUrl}/workshop` }
          ]
        }
      },
      {
        "@type": "ItemList",
        "name": "MOFU 淨寵居香氛課程",
        "description": "手作香氛教室實驗室課程",
        "numberOfItems": courses.length,
        "itemListElement": courses.map((course, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": course.name,
            "description": course.description,
            "provider": {
              "@type": "Organization",
              "name": "MOFU 淨寵居",
              "url": siteUrl
            },
            "offers": {
              "@type": "Offer",
              "price": course.price,
              "priceCurrency": "TWD",
              "availability": "https://schema.org/InStock",
              "url": `${siteUrl}/workshop`
            },
            "timeRequired": course.duration,
            "courseMode": "offline",
            "educationalLevel": course.level,
            "hasCourseInstance": {
              "@type": "CourseInstance",
              "courseMode": "offline",
              "location": {
                "@type": "Place",
                "name": "MOFU 淨寵居香氛實驗室",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "瓊林南路187巷29號",
                  "addressLocality": "新北市",
                  "addressRegion": "新莊區",
                  "postalCode": "242",
                  "addressCountry": "TW"
                }
              }
            }
          }
        }))
      }
    ]
  };

  const handleBookCourse = (courseName: string) => {
    if (courseName) {
      setFormData(prev => ({ ...prev, course: courseName }));
    }
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <SeoHead
        title="新北市新莊區香氛實驗室課程 - 手作香氛蠟燭與調配 | MOFU 淨寵居"
        description="MOFU 淨寵居香氛實驗室位於新北市新莊區，提供專業手作香氛課程，包括香氛蠟燭製作、香氛調配、擴香製作、手工皂製作等。小班制教學，專業講師指導，適合初學者與進階學員。立即預約您的香氛課程。"
        keywords="香氛實驗室,手作香氛課程,蠟燭製作課程,香氛調配,新北市新莊區"
        ogImage={courses[0]?.image}
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/1ffa46fb4fb36d5ad0423eea99dc4dd3.jpg"
            alt="Workshop Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6 w-full">
          <p className="text-sm tracking-[0.4em] uppercase text-[#c9a96e] mb-6 font-medium">MOFU 香氛實驗室</p>
          <h1
            className="text-6xl lg:text-8xl font-bold mb-6 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            探索香氛的藝術
          </h1>
          <p className="text-xl text-white/80 mb-10 max-w-xl mx-auto">
            在專業講師的指導下，創作屬於您的獨特香氛作品
          </p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="#courses"
              className="inline-block px-10 py-4 bg-[#c9a96e] text-white rounded-full font-medium hover:bg-[#b8935a] transition-all duration-300 cursor-pointer whitespace-nowrap text-sm tracking-wide"
            >
              探索課程
            </a>
            <a
              href="#booking"
              className="inline-block px-10 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white rounded-full font-medium hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm tracking-wide"
            >
              立即預約
            </a>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60 animate-bounce">
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <i className="ri-arrow-down-line text-lg"></i>
        </div>
      </section>

      {/* Immersive Courses Section */}
      <div id="courses">
        <CourseImmersive
          courses={courses}
          onBook={handleBookCourse}
        />
      </div>

      {/* Booking Form Section */}
      <section id="booking" className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              預約您的香氛課程
            </h2>
            <p className="text-gray-600 text-lg">
              {currentUser ? '填寫表單，預約記錄將儲存到您的會員中心' : '填寫表單，我們會盡快與您聯繫確認'}
            </p>
            {!currentUser && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700 mb-4">
                <i className="ri-information-line mr-2"></i>已經是會員？
                <button
                  onClick={() => navigate('/login')}
                  className="ml-2 text-[#c9a96e] font-medium hover:underline cursor-pointer"
                >
                  立即登入
                </button>
                即可查看預約記錄
              </div>
            )}
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
            <form onSubmit={handleSubmit} data-readdy-form id="course-booking-form">
              {/* Step 1: Course Selection */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#c9a96e] text-white rounded-full font-bold">1</div>
                  <h3 className="text-xl font-bold text-gray-800">選擇課程</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {courses.map((course) => (
                    <div
                      key={course.id}
                      onClick={() => setFormData({ ...formData, course: course.name })}
                      className={`p-6 border-2 rounded-2xl cursor-pointer transition-all ${
                        formData.course === course.name
                          ? 'border-[#c9a96e] bg-amber-50'
                          : 'border-gray-200 hover:border-[#c9a96e]/50'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-bold text-gray-800 text-lg mb-1">{course.name}</h4>
                          <p className="text-sm text-gray-600">{course.duration}</p>
                        </div>
                        <span className={`w-6 h-6 flex items-center justify-center rounded-full border-2 ${
                          formData.course === course.name
                            ? 'border-[#c9a96e] bg-[#c9a96e]'
                            : 'border-gray-300'
                        }`}>
                          {formData.course === course.name && (
                            <i className="ri-check-line text-white text-sm"></i>
                          )}
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-[#c9a96e]">NT$ {course.price}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 2: Date & Time Selection */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#c9a96e] text-white rounded-full font-bold">2</div>
                  <h3 className="text-xl font-bold text-gray-800">選擇日期與時段</h3>
                </div>
                {!formData.course && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700 mb-4">
                    <i className="ri-information-line mr-2"></i>請先選擇課程，系統將顯示該課程的可預約日期與時間
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Date Picker */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      選擇日期 <span className="text-[#c9a96e]">*</span>
                    </label>
                    <div
                      onClick={() => setShowCalendar(!showCalendar)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-[#c9a96e] transition-colors flex items-center justify-between"
                    >
                      <span className={formData.date ? 'text-gray-800' : 'text-gray-400'}>
                        {formData.date || '請選擇日期'}
                      </span>
                      <i className="ri-calendar-line text-[#c9a96e] text-xl"></i>
                    </div>

                    {/* Calendar Popup */}
                    {showCalendar && (
                      <div className="absolute z-50 mt-2 bg-white rounded-2xl shadow-2xl p-6 w-full md:w-96">
                        {/* Calendar Header */}
                        <div className="flex items-center justify-between mb-6">
                          <button
                            type="button"
                            onClick={() => changeMonth(-1)}
                            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-arrow-left-s-line text-xl"></i>
                          </button>
                          <h4 className="text-lg font-bold text-gray-800">
                            {year} 年 {monthNames[month]}
                          </h4>
                          <button
                            type="button"
                            onClick={() => changeMonth(1)}
                            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-arrow-right-s-line text-xl"></i>
                          </button>
                        </div>

                        {/* Day Names */}
                        <div className="grid grid-cols-7 gap-2 mb-2">
                          {dayNames.map((day) => (
                            <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                              {day}
                            </div>
                          ))}
                        </div>

                        {/* Calendar Days */}
                        <div className="grid grid-cols-7 gap-2">
                          {Array.from({ length: startingDayOfWeek }).map((_, index) => (
                            <div key={`empty-${index}`} />
                          ))}
                          {Array.from({ length: daysInMonth }).map((_, index) => {
                            const day = index + 1;
                            const dateStr = formatDate(year, month, day);
                            const isAvailable = isDateAvailableForCourse(dateStr);
                            const isSelected = formData.date === dateStr;

                            return (
                              <button
                                key={day}
                                type="button"
                                onClick={() => handleDateSelect(year, month, day)}
                                disabled={!isAvailable}
                                className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-all cursor-pointer ${
                                  isSelected
                                    ? 'bg-[#c9a96e] text-white'
                                    : isAvailable
                                    ? 'hover:bg-amber-100 text-gray-700'
                                    : 'text-gray-300 cursor-not-allowed bg-gray-50'
                                }`}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>

                        {/* 課程日期說明 */}
                        {formData.course && (
                          <div className="mt-4 p-3 bg-amber-50 rounded-xl text-xs text-amber-700">
                            <i className="ri-information-line mr-1"></i>
                            {getSelectedCourse()?.is_unlimited_dates
                              ? '灰色日期為該課程不開課的星期'
                              : '僅顯示課程指定可預約日期，灰色日期無法選取'}
                          </div>
                        )}

                        {/* Close Button */}
                        <button
                          type="button"
                          onClick={() => setShowCalendar(false)}
                          className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          關閉
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Time Slots */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      選擇時段 <span className="text-[#c9a96e]">*</span>
                    </label>
                    {(() => {
                      const course = getSelectedCourse();
                      // 客製化自由預約模式
                      if (course?.is_unlimited_dates && course?.booking_mode === 'flexible') {
                        if (!formData.date) {
                          return <div className="text-sm text-gray-400 py-3">請先選擇日期</div>;
                        }
                        if (loadingFlexibleSlots) {
                          return (
                            <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
                              <div className="w-4 h-4 border-2 border-[#c9a96e] border-t-transparent rounded-full animate-spin"></div>
                              正在計算可預約時段...
                            </div>
                          );
                        }
                        if (flexibleSlots.length === 0) {
                          return <div className="text-sm text-gray-400 py-3">此日期沒有可預約時段</div>;
                        }
                        return (
                          <div className="space-y-2">
                            <div className="flex items-center gap-3 text-xs text-gray-500 mb-1">
                              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#c9a96e] inline-block"></span>可選</span>
                              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 inline-block"></span>已預約</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {flexibleSlots.map(({ time, available }) => (
                                <button
                                  key={time}
                                  type="button"
                                  onClick={() => available && setFormData({ ...formData, time })}
                                  disabled={!available}
                                  className={`px-3 py-3 rounded-xl font-medium transition-all text-xs flex flex-col items-center gap-0.5 ${
                                    !available
                                      ? 'bg-red-50 text-red-300 cursor-not-allowed border border-red-100'
                                      : formData.time === time
                                        ? 'bg-[#c9a96e] text-white cursor-pointer border border-[#c9a96e]'
                                        : 'bg-gray-100 text-gray-700 hover:bg-amber-50 hover:border-[#c9a96e]/50 cursor-pointer border border-transparent'
                                  }`}
                                >
                                  <span className="font-semibold">{time.split('-')[0]}</span>
                                  <span className="opacity-70">至 {time.split('-')[1]}</span>
                                  {!available && <span className="text-red-300 text-xs">已預約</span>}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      // 固定班次或特定日期模式
                      const slots = getAvailableTimeSlots();
                      if (slots.length === 0) {
                        return <div className="text-sm text-gray-400 py-3">請先選擇日期</div>;
                      }
                      return (
                        <div className="grid grid-cols-2 gap-3">
                          {slots.map((time) => (
                            <button
                              key={time}
                              type="button"
                              onClick={() => setFormData({ ...formData, time })}
                              className={`px-4 py-3 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap text-sm ${
                                formData.time === time
                                  ? 'bg-[#c9a96e] text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Step 3: Personal Information */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#c9a96e] text-white rounded-full font-bold">3</div>
                  <h3 className="text-xl font-bold text-gray-800">填寫聯絡資訊</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      姓名 <span className="text-[#c9a96e]">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      maxLength={50}
                      disabled={!!currentUser}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#c9a96e] transition-colors text-sm disabled:bg-gray-50"
                      placeholder="請輸入您的姓名"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      手機號碼 <span className="text-[#c9a96e]">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                      required
                      maxLength={10}
                      pattern="[0-9]{10}"
                      disabled={!!currentUser}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#c9a96e] transition-colors text-sm disabled:bg-gray-50"
                      placeholder="請輸入10位數字"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      電子郵件 <span className="text-[#c9a96e]">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      maxLength={100}
                      disabled={!!currentUser}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#c9a96e] transition-colors text-sm disabled:bg-gray-50"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      備註（選填）
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#c9a96e] transition-colors text-sm resize-none"
                      placeholder="如有特殊需求，請在此說明（限500字）"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.notes.length}/500</p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gray-900 text-white rounded-full font-medium text-lg hover:bg-[#c9a96e] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? '預約中...' : '確認預約'}
              </button>

              {submitMessage && (
                <p className={`mt-4 text-center text-sm ${submitMessage.includes('成功') ? 'text-green-600' : 'text-red-600'}`}>
                  {submitMessage}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}